// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: e319fd14-e280-4a94-9a99-00bf321bcb01

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Grid, Stack } from '@mui/material';
import HelpBubble from './HelpBubble';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement,
  Tooltip, Legend, Title,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import store from './store';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, Title);

const PALETTE = ['#0B5FFF', '#2E7D32', '#F5A623', '#D0021B', '#6A1B9A', '#00838F', '#8E24AA', '#EF6C00'];

function Kpi({ label, value, sub }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="overline" sx={{ color: '#666' }}>{label}</Typography>
      <Typography variant="h3" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>{value}</Typography>
      {sub && <Typography variant="body2" sx={{ color: '#888' }}>{sub}</Typography>}
    </Paper>
  );
}

function countBy(rows, fn) {
  const c = {};
  rows.forEach((r) => {
    const k = fn(r);
    if (k) c[k] = (c[k] || 0) + 1;
  });
  return c;
}

export default function Dashboard({ tenantId }) {
  const [apps, setApps] = useState([]);

  useEffect(() => {
    store.listFactSheets(tenantId, { type: 'Application' }).then(setApps);
  }, [tenantId]);

  const stats = useMemo(() => {
    const total = apps.length;
    const avg = total ? Math.round(apps.reduce((a, b) => a + (b.completion || 0), 0) / total) : 0;
    const approved = apps.filter((a) => a.quality === 'Approved').length;
    const migrating = apps.filter((a) => ['Migrate', 'Eliminate'].includes(a.fields?.timeClassification)).length;
    return { total, avg, approved, migrating };
  }, [apps]);

  const time = useMemo(() => countBy(apps, (a) => a.fields?.timeClassification), [apps]);
  const cell = useMemo(() => countBy(apps, (a) => (a.tags || []).find((t) => t.group === 'Cell')?.value), [apps]);
  const sixR = useMemo(() => countBy(apps, (a) => a.fields?.sixRClassification), [apps]);
  const hosting = useMemo(() => countBy(apps, (a) => a.fields?.hostingType), [apps]);

  const doughnut = (obj) => ({
    labels: Object.keys(obj),
    datasets: [{ data: Object.values(obj), backgroundColor: PALETTE }],
  });
  const bar = (obj, label) => ({
    labels: Object.keys(obj),
    datasets: [{ label, data: Object.values(obj), backgroundColor: PALETTE }],
  });
  const barOpts = { plugins: { legend: { display: false } }, maintainAspectRatio: false };
  const pieOpts = { plugins: { legend: { position: 'right' } }, maintainAspectRatio: false };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Application Portfolio Management</Typography>
        <HelpBubble topic="dashboard" />
      </Stack>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={6} md={3}><Kpi label="Total Applications" value={stats.total} /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Avg. Completion" value={`${stats.avg}%`} sub="data quality" /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Approved" value={stats.approved} sub={`of ${stats.total}`} /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Migrate / Eliminate" value={stats.migrating} sub="TIME disposition" /></Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>TIME Classification</Typography>
            <Box sx={{ height: 230 }}><Doughnut data={doughnut(time)} options={pieOpts} /></Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Applications by Cell</Typography>
            <Box sx={{ height: 230 }}><Bar data={bar(cell, 'Apps')} options={barOpts} /></Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>6R Cloud Disposition</Typography>
            <Box sx={{ height: 230 }}><Bar data={bar(sixR, '6R')} options={barOpts} /></Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Hosting Type</Typography>
            <Box sx={{ height: 230 }}><Doughnut data={doughnut(hosting)} options={pieOpts} /></Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
